import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import ErrorMessage from '../../shared/ErrorMessage.js';
import DateInput from '../../shared/inputs/DateInput.js';
import LotScoreChart from '../LotScoreChart.js';
import './WeightLots.css';

const formatScoredAt = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const scoreColor = (score) => {
  if (score > 0) return '#5a7a3a';
  if (score < 0) return '#c0392b';
  return '#888';
};

const nowLocalDatetime = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const WeightLotScores = () => {
  const { lotId }      = useParams();
  const { t }          = useTranslation();
  const [scores,       setScores]       = useState([]);
  const [score,        setScore]        = useState('');
  const [scoredAt,     setScoredAt]     = useState(nowLocalDatetime());
  const [errorMessage, setErrorMessage] = useState('');

  const loadScores = () => {
    Fetch(`api/data/weight.lots.scores?search_fields=lot_id&search_values=${lotId}`, 'get', null,
      (data) => setScores((data || []).sort((a, b) => b.scored_at.localeCompare(a.scored_at)))
    );
  };

  useEffect(() => {
    loadScores();
  }, [lotId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (score === '' || isNaN(parseFloat(score))) return;
    Fetch('api/data/weight.lots.scores', 'post',
      JSON.stringify({ lot_id: Number(lotId), score: parseFloat(score), scored_at: scoredAt }),
      () => { setScore(''); setScoredAt(nowLocalDatetime()); loadScores(); },
      () => setErrorMessage(t('error_saving'))
    );
  };

  return (
    <div className="wld-page">
      <div className="wld-subpage-title">{t('lot_scores')}</div>

      <LotScoreChart points={scores} />

      <div style={{ marginTop: 24 }}>
      {scores.length === 0 ? (
        <div className="wl-empty-note">{t('no_scores')}</div>
      ) : (
        <div className="wl-sessions-list">
          {scores.map((s, i) => (
            <div key={i} className="wl-session-row">
              <span className="wl-session-date">{formatScoredAt(s.scored_at)}</span>
              <span className="wl-session-weight" style={{ color: scoreColor(s.score) }}>
                {s.score > 0 ? `+${s.score}` : s.score}
              </span>
            </div>
          ))}
        </div>
      )}
      </div>

      <form className="wl-inline-form" onSubmit={handleSubmit}>
        <input
          type="number"
          value={score}
          onChange={e => setScore(e.target.value)}
          placeholder={t('score_value')}
          step="0.1"
          required
        />
        <DateInput
          showTime
          value={scoredAt}
          onChange={(v) => setScoredAt(v)}
          required
        />
        <button type="submit" className="wl-inline-btn">+</button>
      </form>
      <ErrorMessage message={errorMessage} />
    </div>
  );
};

export default WeightLotScores;
